import _ from 'lodash'
import Backbone from 'backbone'
import template from './remote-files-chooser.hbs'
import RemoteFilesGardener from './remote-files-gardener'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import FormView from 'scoreboard/components/form-view'
import i18n from 'scoreboard/components/i18n'
import resizeDialogContent from 'scoreboard/components/resize-dialog-content'
import errorHandler from 'scoreboard/components/error-handler'

/**
 * Gardener for FTP, Google, and Zapier connections (#13959)
 */
const RemoteFilesChooser = Backbone.View.extend({

	tagName: 'form',

	className: 'remote-files-chooser documents-tree',

	events: {
		'click .labeled-toggle .option': 'onClickLabeledToggleOption'
	},

	/**
	 * @param {String} [options.connectionId] the connection id to use to get the files
	 * @param {Object} [options.model]
	 */
	initialize: function(options = {}) {
		this.options = options
		this.multipleFiles = this.options.model
			.getDataSourceMetadata('ftpFile.multipleFiles') || false
		this.selectedFile = this.options.model.getDataSourceMetadata('ftpFile.path')

		const MyForm = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyForm({
			el: this.el,
			guard: false,
			submit: this.submit.bind(this),
			allowCleanSubmission: () =>
				// If the user already chose a file and came back to this dialog
				// they should be able to simply close it again
				this.options.model.getDataSourceMetadata('ftpFile.path')
		})
	},

	render: function() {
		this.$el.empty().append(template(this.context()))

		const includeFilesRecursively = this.options.model
			.getDataSourceMetadata('ftpFile.includeFilesRecursively') || false
		this.$('input[name="includeDescendants"]').prop('checked', includeFilesRecursively)

		// Toggle
		this._toggle = new LabeledToggle({
			options: [{
				value: 'singleFile',
				label: i18n.get('admin.importConnections.ftp.singleFile')
			}, {
				value: 'multipleFiles',
				label: i18n.get('admin.importConnections.ftp.multipleFiles')
			}]
		})
		this.$('.toggle-container').append(this._toggle.el)
		this._toggle.render()
		// Make sure the toggle shows whatever was last selected
		this._toggle.val(this.multipleFiles ? 'multipleFiles' : 'singleFile', { silent: false })

		// Toggling forces the gardner to render
		this.toggleMultipleFiles()

		this.formView.start()

		return this
	},

	renderGardener() {
		// #34547 define which files are allowed to be selected. This will filter
		// all others from the file view
		const allowedFileExtensions = [ 'xls', 'xlsx', 'csv' ]
		if (!this.multipleFiles) {
			allowedFileExtensions.push('zip')
		}
		const options = {
			connectionId: this.options.connectionId,
			allowAll: this.multipleFiles,
			allowedFileExtensions: allowedFileExtensions,
			// #37525 only select root if there isn't already a selected file
			autoSelectRoot: !this.selectedFile
		}
		if (this.remoteFilesGardener) {
			this.remoteFilesGardener.insertLoadingDots()
			this.remoteFilesGardener.show(options)
		} else {
			this.remoteFilesGardener = new RemoteFilesGardener(options)
			this.$('.gardener-container').append(this.remoteFilesGardener.el)
			this.remoteFilesGardener.render()
			this.remoteFilesGardener.on('select', e => {
				this.selectedFile = this.getFtpPath()
				this.formView.change(e)
			})
			// #52438 display error message to user if connection offline/timed out
			this.remoteFilesGardener.on('error', error => {
				// #55107 pass in formView so the error message is displayed
				// in the error banner
				errorHandler(error, {formView: this.formView})
			})
		}

		this.highlightSelection()
		this.$('.finder').addClass('finder-light')
		this.$('.tree-container').addClass('tree-container-light')
	},

	context: function() {
		const model = this.options.model
		return {
			filenameRegex: model.getDataSourceMetadata('ftpFile.filenameRegex') || ''
		}
	},

	onClickLabeledToggleOption: function() {
		this.multipleFiles = this.$('.selected').data('value') === 'multipleFiles'
		this.toggleMultipleFiles()
	},

	toggleMultipleFiles: function() {
		this.$('.multi-file-options').toggleClass('is-not-displayed', !this.multipleFiles)

		let chooseFilesLabel = this.multipleFiles ?
			'admin.import.spreadsheet.choose.directory' :
			'admin.import.spreadsheet.select.file'
		this.$('.panel-header h3').html(i18n.get(chooseFilesLabel))

		this.formView.clearErrors()
		this.renderGardener()
		this.trigger('resize')
	},

	submit: function() {
		let regex = this.multipleFiles ? this.$('input[name="regex"]').val() : null
		let includeDescendents = this.multipleFiles ?
			this.$('input[name="includeDescendants"]').is(':checked') : false

		this.trigger('done', {
			connectionId: this.options.connectionId,
			multipleFiles: this.multipleFiles,
			path: this.getFtpPath(),
			filenameRegex: regex,
			includeFilesRecursively: includeDescendents
		})
	},

	test: function() {
		const tree = _.get(this.remoteFilesGardener, 'tree')
		const node = tree.selected()

		if (!node) {
			return i18n.get('admin.importConnections.ftp.noFileSelected')
		}
		if (this.multipleFiles && node.type !== 'folder') {
			// user tried selecting a file in "multi file" mode
			return i18n.get('admin.importConnections.ftp.cannotSelectFile')

		} else if (!this.multipleFiles && node.type === 'folder') {
			// user tried selecting a directory in "single file" mode
			return i18n.get('admin.importConnections.ftp.cannotSelectFolder')
		}
	},

	getFtpPath() {
		const tree = _.get(this.remoteFilesGardener, 'tree')
		let ftpPath = ''
		if (_.isNil(tree)) {
			return ftpPath
		}
		let node = tree.selected()
		while (!_.isNil(node)) {
			if (ftpPath != '') {
				ftpPath = '/' + ftpPath
			}
			ftpPath = node.label + ftpPath
			node = _.isUndefined(node.parentId) ? null : tree.get(node.parentId)
		}
		return ftpPath
	},

	highlightSelection() {
		const existingFile = this.selectedFile
		if (existingFile) {
			this.remoteFilesGardener._onceTreeLoaded(tree => {
				// Since the user can select the root folder, we need to skip
				// splitting the segments, or we'll get 2 empty strings
				let components = existingFile === '/' ? ['/'] : existingFile.split('/')
				let nodes = tree.get()
				for (let i=0; i<components.length; i++) {
					let node = _.find(nodes, node => {
						let label = tree.get(node.id).label
						return label === components[i]
					})
					if (node) {
						// if we're on the last level of the tree which contains
						// the spreadsheet to highlight
						if (i == components.length - 1) {
							this.makeTreeSelection(tree, node)
						} else {
							nodes = tree.children(node)
						}
					} else {
						// the file or directory must have been deleted, so
						// we don't have anything to highlight
						break
					}
				}
			})
		}
	},

	makeTreeSelection(tree, node) {
		if (node) {
			tree.select(node.id, { silent: true, animate: false })
		}
	},

	resize: function(maxHeight) {
		maxHeight -= this.$('.multi-file-options').outerHeight(true)
		resizeDialogContent({
			dialog: this.el,
			maxHeight,
			target: this.$('.object-selector')
		})
	}
})

export default RemoteFilesChooser

RemoteFilesChooser.__filename__ = 'imports/remote-files-chooser.js'
